/**
 * onboardingContent.js — the ONE keyed content source for the shared
 * onboarding system (Phase 1, Jun 2026).
 *
 * Every onboarding surface lives here as one entry. Adding a new surface
 * later is a CONTENT addition to this object — never a new build. The
 * OnboardingFlow component and the useOnboarding hook read from here; no
 * surface defines its own steps inline.
 *
 * Entry shape:
 *   [surfaceKey]: {
 *     version: "v1",              // bump to re-run this surface's tour
 *     title: "…",                 // panel heading
 *     steps: [                    // 1..n steps
 *       { title: "…", body: "…" },
 *     ],
 *   }
 *
 * VERSIONING: the `version` string is what gets written to
 * `users.onboarding_seen[surfaceKey]` when a teacher completes/dismisses.
 * A stored version that differs from (or is missing vs.) the version here
 * means "show it". Bumping the version re-runs ONLY that surface.
 *
 * Phase 1 intentionally ships NO real surface content. First Run
 * (Phase 2), first-touch Planner / Supply Plans / Build from Resource
 * (Phase 3), and cold-start empty states (Phase 4) each add their keyed
 * content here under their own green light.
 */

export const ONBOARDING_CONTENT = {
  // ── First Run (Phase 2) ────────────────────────────────────────────
  // Consumed by components/onboarding/FirstRunFlow.jsx. All strings are
  // FINAL and verbatim per the founder spec — do not reword. Dynamic
  // values in {braces} are interpolated by the flow at runtime.
  first_run: {
    version: "v1",
    copy: {
      // Step 1 — grade & subject
      step1_headline: "Let's build you a real lesson.\nYou keep what we make.",
      step1_question_a: "What grade, and what subject?",
      step1_question_c: "Which one are we building for today?",
      step1_note_c: "You can build for the rest later.",
      step1_button: "Start building",
      // Step 2 — skill (pre-selected)
      step2: "Every lesson here starts with a skill.\nWe've picked {skill_name} — change it if you'd rather.",
      step2_button: "Next",
      // Step 3 — expectation
      step3: "Now the curriculum.\nThree real Grade {grade} {subject} expectations. Pick the one closest to where your class is.",
      step3_link: "Looking for a different one? You'll get the full list after this.",
      step3_button: "Next",
      // Step 4 — generate
      step4_button: "Build my lesson",
      // Step 5 — three call-outs on the finished lesson
      callout_a: "This is the skill, in the lesson.\nNot a label on top of it. {skill_short_name} is built into what students actually do here.",
      callout_b: "And this is the Ontario expectation.\nNamed, in full.",
      callout_c: "Print it, export it, or send it to your planner.\nIt's a finished lesson. Teach it tomorrow if you want.",
      // Step 6 — endings
      ending_new_trial: "That's the whole loop.\nThe second one is faster, because we already know your grade and subject.",
      ending_new_trial_primary: "Build another",
      ending_new_trial_secondary: "Or go have a look around.",
      ending_existing: "That's the loop.\nYou already know your way around — this was just a quick look at how it hangs together now.",
      ending_existing_button: "Done",
      ending_free: "That's yours. Keep it.",
      ending_free_primary: "Save to my free lessons",
      ending_free_secondary: "Want your grades, your subjects, and every skill? The 7-day trial is free and doesn't need a card.",
      // Dismiss / replay
      dismiss_confirm: "You can pick this up again from the help icon.",
      replay_label: "Show me how this works",
    },
  },

  // (Phase 3–4 surfaces get added here.)
};

/** Return the content entry for a surface, or null if none is defined. */
export const getOnboardingSurface = (surfaceKey) =>
  (surfaceKey && ONBOARDING_CONTENT[surfaceKey]) || null;

/** The current content version for a surface, or null if undefined. */
export const getOnboardingVersion = (surfaceKey) => {
  const s = getOnboardingSurface(surfaceKey);
  return s ? s.version : null;
};
